import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CalendarDays, Check, ChevronRight, Copy, ExternalLink, FileText, Home, MapPin, Maximize2, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, IconInput } from "@/components/forms/FormField";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PreviewLightbox from "@/components/window/PreviewLightbox";
import { formatINR } from "./QuotationItems";
import { toast } from "sonner";

export const formatDay = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const card = "bg-card border border-border rounded-2xl p-5";

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
      <dt className="text-sm text-muted-foreground flex-1">{label}</dt>
      <dd className="text-sm text-foreground text-right truncate max-w-[60%]">{children}</dd>
    </div>
  );
}

export function CustomerProjectCard({ customerName, location, date, number, rooms }) {
  return (
    <section className={card} data-testid="quotation-customer-card">
      <h2 className="font-serif text-lg text-foreground mb-1">Customer &amp; Project</h2>
      <dl>
        <Row icon={UserRound} label="Customer">{customerName}</Row>
        <Row icon={MapPin} label="Location">{location || "—"}</Row>
        <Row icon={CalendarDays} label="Quotation Date">{date}</Row>
        <Row icon={FileText} label="Quotation ID">#{number}</Row>
      </dl>
      <dl className="mt-2 pt-2 border-t border-border">
        <Row icon={Home} label={rooms.length === 1 ? "Room" : "Rooms"}>{rooms.join(", ") || "—"}</Row>
      </dl>
    </section>
  );
}

// The quotation's line previews, shown with the same gallery as the window workspace.
export function DesignPreviewCard({ items }) {
  const previews = useMemo(
    () =>
      items
        .filter((i) => i.previewUrl)
        .map((i) => ({ _id: i._id, status: "SUCCESS", previewUrl: i.previewUrl, metadata: { specs: i.specSnapshot } })),
    [items],
  );
  const [currentId, setCurrentId] = useState(previews[0]?._id);
  const [open, setOpen] = useState(false);
  const stripRef = useRef(null);
  const current = previews.find((p) => p._id === currentId) || previews[0];

  useEffect(() => {
    if (!previews.some((p) => p._id === currentId)) setCurrentId(previews[0]?._id);
  }, [previews, currentId]);

  if (!previews.length) return null;
  return (
    <section className={card} data-testid="quotation-design-preview">
      <h2 className="font-serif text-lg text-foreground mb-3">Design Preview</h2>
      <div className="relative rounded-xl overflow-hidden bg-secondary">
        <button type="button" onClick={() => setOpen(true)} className="block w-full cursor-zoom-in" aria-label="Open design preview">
          <img src={current.previewUrl} alt="Design preview" className="w-full aspect-[3/2] object-cover" />
        </button>
        <button
          type="button"
          aria-label="View full screen"
          data-testid="quotation-preview-fullscreen"
          onClick={() => setOpen(true)}
          className="absolute top-2.5 right-2.5 h-8 w-8 rounded-full bg-card/95 shadow flex items-center justify-center hover:bg-card"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      </div>
      {previews.length > 1 && (
        <div className="relative mt-3">
          <div ref={stripRef} className="flex gap-2 overflow-x-auto p-0.5 pr-8">
            {previews.map((p) => (
              <button
                key={p._id}
                type="button"
                aria-pressed={p._id === current._id}
                onClick={() => setCurrentId(p._id)}
                className={cn(
                  "shrink-0 h-14 w-[4.5rem] rounded-lg overflow-hidden transition-all",
                  p._id === current._id ? "ring-2 ring-accent ring-offset-1 ring-offset-card" : "opacity-75 hover:opacity-100",
                )}
              >
                <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          {previews.length > 3 && (
            <button
              type="button"
              aria-label="More previews"
              onClick={() => stripRef.current?.scrollBy({ left: 160, behavior: "smooth" })}
              className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-card border border-border shadow flex items-center justify-center"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <PreviewLightbox open={open} setOpen={setOpen} previews={previews} currentId={current._id} onChange={setCurrentId} />
    </section>
  );
}

export function ApprovalDecisionCard({ quotation, items, onApprove, onRequestChanges, isPending }) {
  const [changeNotes, setChangeNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const hasManualDiscount = (quotation.discountAmount || 0) > 0;
  const roomCount = new Set(items.map((i) => i.roomId)).size;
  const aiDesignsCount = items.filter((i) => i.finalAiPreviewFileId).length;

  return (
    <section className={cn(card, "border-accent/40 ring-1 ring-accent/15")} data-testid="approval-decision-card">
      <p className="text-[11px] font-semibold tracking-wider text-accent uppercase">Awaiting your approval</p>
      <p className="font-serif text-3xl text-foreground mt-1 tabular-nums">{formatINR(quotation.grandTotal)}</p>
      <p className="text-xs text-muted-foreground mb-4">Grand Total</p>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          [items.length, "Items"],
          [roomCount, "Rooms"],
          [aiDesignsCount, "AI Designs"],
        ].map(([n, label]) => (
          <div key={label} className="text-center bg-secondary/70 rounded-lg py-2.5">
            <p className="font-medium text-foreground">{n}</p>
            <p className="text-[10px] text-muted-foreground uppercase mt-0.5">{label}</p>
          </div>
        ))}
      </div>
      <div className="space-y-1.5 mb-4 text-sm">
        {hasManualDiscount && (
          <p className="flex items-center gap-2 text-[hsl(41_60%_32%)]" data-testid="approval-flag-discount">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Manual discount applied ({formatINR(quotation.discountAmount)})
          </p>
        )}
        <p className="flex items-center gap-2 text-success"><Check className="h-3.5 w-3.5 shrink-0" /> Costing verified</p>
        <p className="flex items-center gap-2 text-success"><Check className="h-3.5 w-3.5 shrink-0" /> Customer information verified</p>
        <p className="flex items-center gap-2 text-success"><Check className="h-3.5 w-3.5 shrink-0" /> GST calculated ({formatINR(quotation.gstAmount)})</p>
      </div>
      {showNotes && (
        <Textarea data-testid="change-notes-input" placeholder="Reason for requesting changes" value={changeNotes} onChange={(e) => setChangeNotes(e.target.value)} className="mb-3" />
      )}
      <div className="flex flex-col gap-2">
        <Button data-testid="approve-quotation-button" disabled={isPending} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground" onClick={onApprove}>
          Approve Quotation
        </Button>
        <Button
          variant="outline"
          data-testid="request-changes-button"
          disabled={showNotes && !changeNotes.trim()}
          onClick={() => (showNotes ? onRequestChanges(changeNotes) : setShowNotes(true))}
        >
          {showNotes ? "Send change request" : "Request Changes"}
        </Button>
      </div>
    </section>
  );
}

export function SharedLinkCard({ url }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy; select the link and copy it manually");
    }
  };
  return (
    <section className={card} data-testid="public-quote-link-box">
      <h2 className="font-serif text-lg text-foreground mb-2">Shared Link</h2>
      <code className="font-mono-spec text-xs break-all block bg-secondary rounded-lg px-2.5 py-2" data-testid="public-quote-url">{url}</code>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" className="flex-1" data-testid="copy-public-link-button" onClick={copy}>
          <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
        </Button>
        <Button variant="outline" size="sm" className="flex-1" asChild>
          <a href={url} target="_blank" rel="noreferrer" data-testid="open-public-link">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open
          </a>
        </Button>
      </div>
    </section>
  );
}

export function FinalizeDialog({ open, setOpen, defaults, onSubmit, pending }) {
  const [form, setForm] = useState(defaults);
  useEffect(() => {
    if (open) setForm(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const valid = form.shopName.trim() && form.customerName.trim() && form.projectName.trim();
  const input = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="finalize-dialog">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) onSubmit(form);
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-normal">Finalize Quotation</DialogTitle>
            <DialogDescription>Generates the PDF with these names. Items and pricing are locked after this.</DialogDescription>
          </DialogHeader>
          <div className="mt-5 space-y-4">
            <Field label="Shop Name" required htmlFor="fin-shop"><IconInput id="fin-shop" icon={Home} data-testid="finalize-shopname-input" {...input("shopName")} /></Field>
            <Field label="Customer Name" required htmlFor="fin-customer"><IconInput id="fin-customer" icon={UserRound} data-testid="finalize-customername-input" {...input("customerName")} /></Field>
            <Field label="Project Name" required htmlFor="fin-project"><IconInput id="fin-project" icon={FileText} data-testid="finalize-projectname-input" {...input("projectName")} /></Field>
          </div>
          <Button type="submit" data-testid="finalize-quotation-button" disabled={!valid || pending} className="mt-6 w-full bg-accent hover:bg-accent/90 text-accent-foreground">
            {pending ? "Generating PDF…" : "Finalize & Generate PDF"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ShareDialog({ open, setOpen, status, defaultEmail, publicUrl, customerPhone, onShare, pending }) {
  const [email, setEmail] = useState("");
  useEffect(() => {
    if (open) setEmail(defaultEmail || "");
  }, [open, defaultEmail]);
  const shared = status === "SHARED" && publicUrl;
  const whatsapp = shared
    ? `https://wa.me/${(customerPhone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Here is your quotation: ${publicUrl}`)}`
    : null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-normal">Share Quotation</DialogTitle>
          <DialogDescription>
            {shared ? "This quotation has a secure link. Send it to the customer however suits them." : "Creates a secure link for the customer and emails it with the PDF attached."}
          </DialogDescription>
        </DialogHeader>
        {shared ? (
          <div className="mt-4 space-y-3">
            <SharedLinkCard url={publicUrl} />
            {whatsapp && (
              <Button asChild variant="outline" className="w-full">
                <a href={whatsapp} target="_blank" rel="noreferrer">Send on WhatsApp</a>
              </Button>
            )}
          </div>
        ) : (
          <form
            className="mt-4"
            onSubmit={(e) => {
              e.preventDefault();
              onShare(email.trim() || undefined);
            }}
          >
            <Field label="Customer email" optional htmlFor="share-email">
              <IconInput id="share-email" icon={UserRound} type="email" data-testid="share-customer-email-input" placeholder="Defaults to the customer's email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <p className="text-xs text-muted-foreground mt-1.5">Leave empty to use the email on the customer’s record, if there is one.</p>
            <Button type="submit" data-testid="share-quotation-button" disabled={pending} className="mt-5 w-full bg-accent hover:bg-accent/90 text-accent-foreground">
              {pending ? "Sharing…" : "Share Securely"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
